import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import { PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { utilities as toolsUtilities } from '@cornerstonejs/tools';
import { Dicom } from '@/models/dicom';
import { useGetMediaQuery } from '@/state/slices/media';
import {
  selectActiveMeasurement,
  selectMeasurementStatus,
} from '@/state/slices/measurementSession';
import { reportPaneLoadFailure } from '@/features/study-detail/history/viewerNavigation';
import { ViewerCanvas } from '../ViewerCanvas';
import { ToolHint } from './ToolHint';
import { SelectedAnnotationLabel } from './SelectedAnnotationLabel';
import { MeasurementList } from './MeasurementList';
import styles from './ViewerPane.module.css';
import { FrameSlider } from '../FrameSlider';
import { useViewerContext } from '../ViewerContext';
import { usePlaybackContext } from '../PlaybackContext';
import { useMeasurementCapture } from '../hooks/useMeasurementCapture';
import { useSimpsonOverlay } from '../hooks/useSimpsonOverlay';
import { useDopplerBaselineOverlay } from '../hooks/useDopplerBaselineOverlay';
import { viewerCacheManager } from '../viewerCacheManager';
import { usePaneNativeFps } from './hooks/usePaneNativeFps';
import { useViewportSetup } from './hooks/useViewportSetup';
import { useFrozenPlaybackIntent } from './hooks/useFrozenPlaybackIntent';
import { useVideoPlayback } from './hooks/useVideoPlayback';
import { useDeselectOnFrameLeave } from './hooks/useDeselectOnFrameLeave';
import { useAnnotationHydration } from './hooks/useAnnotationHydration';
import { useAnnotatedFrames } from './hooks/useAnnotatedFrames';
import { useMeasurementToolActivation } from './hooks/useMeasurementToolActivation';
import { useViewerHotkeys } from './hooks/useViewerHotkeys';
import { useViewerLoading } from '../ViewerLoadingContext';
import { useViewPrediction } from './hooks/useViewPrediction';
import { usePhaseKeyframes } from './hooks/usePhaseKeyframes';

interface ViewerPaneProps {
  viewportId: string;
  dicom: Dicom | null;
  paneIndex: number;
  onClick: () => void;
  isSelected: boolean;
  isPinned?: boolean;
  onUnpin?: () => void;
  onPin?: () => void;
  onDropDicom?: (dicom: Dicom) => void;
}

// Generate a unique instance ID for this component mount
// This ensures each ViewerPane instance gets a completely fresh Cornerstone viewport
const generateInstanceId = () => Math.random().toString(36).substring(2, 11);

export function ViewerPane({
  viewportId: baseViewportId,
  dicom,
  paneIndex,
  onClick,
  isSelected,
  isPinned = false,
  onUnpin,
  onPin,
  onDropDicom,
}: ViewerPaneProps) {
  // Instance ID - new on each mount
  const [instanceId] = useState(generateInstanceId);

  // CRITICAL: Use unique viewportId per instance to prevent Cornerstone stale state
  const viewportId = `${baseViewportId}-${instanceId}`;

  const {
    studyUuid,
    renderingEngine,
    activeViewportId,
    setActiveViewportId,
    activeTool,
    changeTool,
    deactivateTool,
    setupToolGroupForViewport,
    cleanupToolGroupForViewport,
    registerViewportId,
    unregisterViewportId,
    getPaneCineSpeed,
    setPaneNativeFps,
    autoplay,
    startFrame,
    voiMapRef,
    viewportInitialCameraRef,
    getDicomByUuid,
    dicomNumberByUuid,
    reportPaneLoadResult,
  } = useViewerContext();

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    const { dataTransfer } = e;
    if (!dataTransfer.types.includes('dicom/uuid')) return;
    e.preventDefault();
    dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const uuid = e.dataTransfer.getData('dicom/uuid');
    if (!uuid) return;
    const dropped = getDicomByUuid(uuid);
    if (dropped) onDropDicom?.(dropped);
  };

  const contextMenuItems = isPinned
    ? [{ key: 'unpin', label: 'Unpin pane' }]
    : [];

  const { subscribeToVideoPause, subscribeToVideoToggle, pauseAll } =
    usePlaybackContext();

  // This pane's own playback speed (1 = native). The cine FPS control edits
  // only the selected pane's value, so changing it never affects other panes.
  const cineSpeedMultiplier = getPaneCineSpeed(paneIndex);

  const frameCount = dicom?.numberOfFrames ?? 1;
  const isMultiFrame = frameCount > 1;
  const isVideo = isMultiFrame;

  const dicomUuid = dicom?.uuid;
  const {
    data: mediaData,
    isLoading: isLoadingMedia,
    error: errorMedia,
  } = useGetMediaQuery(
    { study_uuid: studyUuid, paginate: false },
    {
      skip: !studyUuid,
      selectFromResult: ({ data, isLoading, error }) => ({
        data: data
          ? {
              ...data,
              results: (data.results ?? []).filter(
                (m) => m.dicomUuid === dicomUuid
              ),
            }
          : undefined,
        isLoading,
        error,
      }),
    }
  );

  // Get the video URL from media response (for multi-frame)
  const videoUrl = useMemo(() => {
    if (!isVideo || !mediaData?.results?.length) return null;
    const videoMedia = mediaData.results.find((m) => m.type === 'VIDEO');
    return videoMedia?.file ?? null;
  }, [isVideo, mediaData]);

  // Get the image URL from media response (for single-frame)
  const imageUrl = useMemo(() => {
    if (isVideo || !mediaData?.results?.length) return null;
    const imageMedia = mediaData.results.find((m) => m.type === 'IMAGE');
    return imageMedia?.file ?? null;
  }, [isVideo, mediaData]);

  const cornerstoneRef = useRef<HTMLDivElement>(null);

  // DICOM transition tracking
  const [prevDicomUuid, setPrevDicomUuid] = useState<string | undefined>(
    dicom?.uuid
  );
  const [isDicomTransitioning, setIsDicomTransitioning] = useState(false);
  const loadedDicomUuidRef = useRef<string | undefined>(undefined);
  const viewportCreatedRef = useRef(false);
  const videoMetadataProviderRef = useRef<
    ((type: string, imageId: unknown) => unknown) | null
  >(null);

  // Track previous viewport type to detect type changes vs. same-type reuse
  const prevIsVideoRef = useRef(isVideo);

  // Handle DICOM change — track previous state for effects.
  // No skeleton is shown here; snapshot canvases provide visual continuity
  // for both same-type reuse and type-change transitions.
  if (dicom?.uuid !== prevDicomUuid) {
    setPrevDicomUuid(dicom?.uuid);
    prevIsVideoRef.current = isVideo;
  }

  const { autoplay: paneAutoplay, startFrame: paneStartFrame } =
    useFrozenPlaybackIntent({ isPinned, autoplay, startFrame });

  // --- Viewport setup (metadata, creation, content loading) ---
  const {
    viewport,
    isLoadingViewport,
    viewportError,
    failedDicomUuid,
    isVideoViewport,
    frameImageIds,
    memoizedImageIds,
  } = useViewportSetup({
    dicom,
    viewportId,
    renderingEngine,
    cornerstoneRef,
    videoUrl,
    imageUrl,
    isVideo,
    frameCount,
    autoplay: paneAutoplay,
    startFrame: paneStartFrame,
    viewportCreatedRef,
    loadedDicomUuidRef,
    videoMetadataProviderRef,
    setIsDicomTransitioning,
    voiMapRef,
    viewportInitialCameraRef,
  });

  // Native FPS of this clip — backend fields first, else derived from the
  // loaded video. Reported to the provider so the cine FPS control can read
  // the selected pane's rate.
  const nativeFps = usePaneNativeFps(dicom, viewport, isVideoViewport);
  useEffect(() => {
    setPaneNativeFps(paneIndex, nativeFps);
  }, [paneIndex, nativeFps, setPaneNativeFps]);

  // --- Video playback (subscriptions, tracking, controls) ---
  const {
    currentFrame,
    isPlaying,
    handleFrameChange,
    pause,
    togglePlayPause,
    handlePreviousFrame,
    handleNextFrame,
    currentFrameRef,
    isPlayingRef,
  } = useVideoPlayback({
    viewport,
    isVideoViewport,
    viewportId,
    frameCount,
    cineSpeedMultiplier,
    subscribeToVideoPause,
    subscribeToVideoToggle,
    deactivateTool,
  });

  // --- Tool group setup & viewport registration ---
  useEffect(() => {
    if (viewport) {
      setupToolGroupForViewport(viewportId);
      return () => cleanupToolGroupForViewport(viewportId);
    }
    return () => {};
  }, [
    viewport,
    viewportId,
    setupToolGroupForViewport,
    cleanupToolGroupForViewport,
  ]);

  useEffect(() => {
    registerViewportId(viewportId);
    return () => unregisterViewportId(viewportId);
  }, [viewportId, registerViewportId, unregisterViewportId]);

  // --- Image cache tracking ---
  useEffect(() => {
    if (memoizedImageIds && memoizedImageIds.length > 0 && dicom?.uuid) {
      viewerCacheManager.trackDicomForViewport(
        viewportId,
        dicom.uuid,
        memoizedImageIds
      );
      return () => {
        viewerCacheManager.clearViewport(viewportId);
      };
    }
    return () => {};
  }, [viewportId, memoizedImageIds, dicom?.uuid]);

  // --- Calibrate image spacing for measurement tools ---
  useEffect(() => {
    if (!viewport || !renderingEngine || !dicom) return;
    if (isVideo !== isVideoViewport) return;

    // Render-ready spacing is derived at ingest (BE) — already abs()'d, cm→mm
    // converted, and isotropic-collapsed for mixed-unit ultrasound (M-mode,
    // spectral Doppler). Pass straight through; coerce null→undefined for
    // cornerstone's signature.
    const rowPixelSpacing = dicom.rowPixelSpacingMm ?? undefined;
    const columnPixelSpacing = dicom.columnPixelSpacingMm ?? undefined;

    if (isVideoViewport) {
      const videoImageId = `video:${dicom.uuid}`;
      try {
        toolsUtilities.calibrateImageSpacing(videoImageId, renderingEngine, {
          rowPixelSpacing,
          columnPixelSpacing,
          // @ts-expect-error CalibrationTypes.USER is the correct value but types don't match
          type: 'User',
        });
      } catch {
        // Video calibration failed - may happen if viewport not ready
      }
    } else if (memoizedImageIds) {
      memoizedImageIds.forEach((imageId) => {
        try {
          toolsUtilities.calibrateImageSpacing(imageId, renderingEngine, {
            rowPixelSpacing,
            columnPixelSpacing,
            // @ts-expect-error CalibrationTypes.USER is the correct value but types don't match
            type: 'User',
          });
        } catch (err) {
          throw new Error(
            `[ViewerPane ${viewportId}] Failed to calibrate image: ${err}`
          );
        }
      });
    }
  }, [
    viewport,
    renderingEngine,
    memoizedImageIds,
    dicom,
    isVideo,
    isVideoViewport,
    viewportId,
  ]);

  // --- Measurement capture & Simpson overlay ---
  const activeMeasurement = useSelector(selectActiveMeasurement);
  const measurementStatus = useSelector(selectMeasurementStatus);

  const rowPixelSpacingMm = dicom?.rowPixelSpacingMm;
  const columnPixelSpacingMm = dicom?.columnPixelSpacingMm;
  const simpsonPixelSpacing = useMemo(() => {
    if (rowPixelSpacingMm && columnPixelSpacingMm) {
      return (rowPixelSpacingMm + columnPixelSpacingMm) / 2;
    }
    return 1;
  }, [rowPixelSpacingMm, columnPixelSpacingMm]);

  useMeasurementCapture(
    viewport ? viewportId : undefined,
    currentFrame,
    simpsonPixelSpacing,
    dicom?.uuid,
    activeViewportId,
    activeTool
  );

  useSimpsonOverlay({
    viewport,
    renderingEngine,
    currentFrame,
    dicomUuid: dicom?.uuid,
  });

  useDopplerBaselineOverlay({
    viewport,
    currentFrame,
    activeTool,
  });

  // --- Measurement tool activation ---
  useMeasurementToolActivation({
    viewport,
    viewportId,
    activeViewportId,
    activeTool,
    changeTool,
    deactivateTool,
    pauseAll,
  });

  // Navigating away from the selected annotation's frame deselects it
  // (AP-764 I7 turn-off rules) — scrub, arrow keys, or playback alike.
  useDeselectOnFrameLeave({
    dicomUuid: dicom?.uuid,
    isVideoViewport,
    currentFrame,
  });

  // --- Annotation hydration (per DICOM, triggered by report load) ---
  const viewPrediction = useViewPrediction(studyUuid, dicom?.uuid);
  useAnnotationHydration({
    viewport,
    viewportId,
    studyUuid,
    dicomUuid: dicom?.uuid,
    videoUrl,
    loadedDicomUuidRef,
    isLoadingViewport,
    stackImageId: isVideoViewport ? null : (memoizedImageIds?.[0] ?? null),
    // Gate hydration until loaded so it reads the real FoR (videoElement.src).
    isViewportReady: !!viewport && !isLoadingViewport,
    // Pass through only when both axes are calibrated — hydration seeds
    // Area + L using this value and falls back to skipping when undefined.
    pixelSpacing:
      rowPixelSpacingMm && columnPixelSpacingMm
        ? simpsonPixelSpacing
        : undefined,
    view: viewPrediction?.view,
  });

  // --- Report pane loading state to the top-level loading bar ---
  const { reportPaneLoading } = useViewerLoading();

  const annotatedFrames = useAnnotatedFrames(dicom?.uuid);
  const phaseKeyframes = usePhaseKeyframes(studyUuid, dicom?.uuid, frameCount);

  // --- Hotkeys, cursor, wheel navigation ---
  const isActive = activeViewportId === viewportId;
  const { handleMouseEnter, handleMouseLeave, handleClick } = useViewerHotkeys({
    viewport,
    viewportId,
    isActive,
    activeTool,
    cornerstoneRef,
    changeTool,
    deactivateTool,
    pauseAll,
    setActiveViewportId,
    onClick,
    isVideo,
    frameCount,
    currentFrameRef,
    isPlayingRef,
    pause,
    handleFrameChange,
  });

  // --- Loading state (computed before early returns for hook compliance) ---
  // The last condition (url && !viewport) means "we have a URL but the viewport
  // hasn't been created yet". When viewportError is set, setup has failed and
  // the viewport will never be created — exclude it so the error overlay shows.
  const isLoading =
    isDicomTransitioning ||
    isLoadingMedia ||
    isLoadingViewport ||
    (!viewportError &&
      (isVideo ? videoUrl && !viewport : imageUrl && !viewport));

  useEffect(() => {
    reportPaneLoading(paneIndex, !!isLoading);
    return () => reportPaneLoading(paneIndex, false);
  }, [reportPaneLoading, paneIndex, isLoading]);

  // Report once per DICOM whether it loaded, so a Shift+drag placement that
  // fails can be undone (AP-804).
  const reportedLoadUuidRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const uuid = dicom?.uuid;
    if (!uuid || isLoadingMedia || reportedLoadUuidRef.current === uuid) return;
    const failed =
      !!errorMedia ||
      !(isVideo ? videoUrl : imageUrl) ||
      failedDicomUuid === uuid;
    if (!failed && loadedDicomUuidRef.current !== uuid) return;
    reportedLoadUuidRef.current = uuid;
    reportPaneLoadResult(paneIndex, uuid, !failed);
  }, [
    dicom?.uuid,
    isLoadingMedia,
    errorMedia,
    isVideo,
    videoUrl,
    imageUrl,
    failedDicomUuid,
    viewport,
    isDicomTransitioning,
    reportPaneLoadResult,
    paneIndex,
  ]);

  // AP-773 (R19): a history step waiting for this DICOM applies anyway when
  // the media or the viewport fails to load it.
  useEffect(() => {
    if (dicomUuid && (errorMedia || viewportError)) {
      reportPaneLoadFailure(viewportId, dicomUuid);
    }
  }, [viewportId, dicomUuid, errorMedia, viewportError]);

  // Track whether this pane has ever successfully rendered content.
  // Used to show the skeleton only on first load (not on subsequent DICOM navigations).
  const hasEverRenderedRef = useRef(false);
  useEffect(() => {
    if (viewport && !isLoading) {
      hasEverRenderedRef.current = true;
    }
  }, [viewport, isLoading]);

  // --- Render ---

  // Empty state (no DICOM assigned)
  if (!dicom) {
    return (
      <Dropdown
        menu={{
          items: contextMenuItems,
          onClick: ({ key }) => {
            if (key === 'unpin') onUnpin?.();
          },
        }}
        trigger={['contextMenu']}
      >
        <div
          className={styles.viewerPane}
          role="button"
          tabIndex={0}
          aria-label={`Viewport ${paneIndex + 1}`}
          data-selected={isSelected}
          data-pinned={isPinned}
          data-drag-over={isDragOver}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleClick();
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isPinned && (
            <div
              className={styles.topRightOverlay}
              data-pinned={isPinned || undefined}
            >
              <button
                type="button"
                className={styles.pinIndicator}
                onClick={(e) => {
                  e.stopPropagation();
                  onUnpin?.();
                }}
                title="Unpin pane"
                aria-label="Unpin pane"
              >
                <PushpinFilled />
              </button>
            </div>
          )}
          <div className={styles.emptyViewportState}>
            <span>Viewport {paneIndex + 1}</span>
          </div>
        </div>
      </Dropdown>
    );
  }

  // No content state
  const hasNoContent = isVideo
    ? !videoUrl && !isLoadingMedia
    : !imageUrl && !isLoadingMedia;

  if (hasNoContent) {
    return (
      <Dropdown
        menu={{
          items: contextMenuItems,
          onClick: ({ key }) => {
            if (key === 'unpin') onUnpin?.();
          },
        }}
        trigger={['contextMenu']}
      >
        <div
          className={styles.viewerPane}
          role="button"
          tabIndex={0}
          aria-label={`Viewport ${paneIndex + 1}`}
          data-selected={isSelected}
          data-pinned={isPinned}
          data-drag-over={isDragOver}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleClick();
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isPinned && (
            <div
              className={styles.topRightOverlay}
              data-pinned={isPinned || undefined}
            >
              <button
                type="button"
                className={styles.pinIndicator}
                onClick={(e) => {
                  e.stopPropagation();
                  onUnpin?.();
                }}
                title="Unpin pane"
                aria-label="Unpin pane"
              >
                <PushpinFilled />
              </button>
            </div>
          )}
          <div className={styles.emptyViewportState}>
            <span>{isVideo ? 'No video available' : 'No image available'}</span>
          </div>
        </div>
      </Dropdown>
    );
  }

  const error = errorMedia;

  return (
    <Dropdown
      menu={{
        items: contextMenuItems,
        onClick: ({ key }) => {
          if (key === 'unpin') onUnpin?.();
        },
      }}
      trigger={['contextMenu']}
    >
      <div
        className={styles.viewerPane}
        role="button"
        tabIndex={0}
        aria-label={`Viewport ${paneIndex + 1}`}
        data-selected={isSelected}
        data-pinned={isPinned}
        data-drag-over={isDragOver}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={styles.topRightOverlay}
          data-pinned={isPinned || undefined}
        >
          <SelectedAnnotationLabel
            dicomUuid={dicom?.uuid}
            stackImageId={
              isVideoViewport ? null : (memoizedImageIds?.[0] ?? null)
            }
          />
          {isPinned ? (
            <button
              type="button"
              className={styles.pinIndicator}
              onClick={(e) => {
                e.stopPropagation();
                onUnpin?.();
              }}
              title="Unpin pane"
              aria-label="Unpin pane"
            >
              <PushpinFilled />
            </button>
          ) : (
            onPin && (
              <button
                type="button"
                className={styles.pinIndicator}
                data-unpinned
                onClick={(e) => {
                  e.stopPropagation();
                  onPin();
                }}
                title="Pin pane"
                aria-label="Pin pane"
              >
                <PushpinOutlined />
              </button>
            )
          )}
          <div className={styles.overlayBadge}>
            {dicomNumberByUuid.get(dicom.uuid) ?? dicom.index}
          </div>
        </div>
        {isLoading && !hasEverRenderedRef.current && (
          <div className={styles.viewerSkeleton} />
        )}

        {/* Viewport-init failures render an empty pane (logged, not shown).
            Only a media-load error surfaces a message. */}
        {error && !isLoading && (
          <div className={styles.errorOverlay}>
            <div>
              <span style={{ fontSize: '14px', fontWeight: 500 }}>
                Failed to load media
              </span>
            </div>
          </div>
        )}

        {/* Render canvas when we have content (video URL or image URL) */}
        {(videoUrl || imageUrl) && (
          <>
            <ViewerCanvas
              key={`canvas-${viewportId}-${instanceId}`}
              cornerstoneRef={cornerstoneRef}
              isRendered={
                !!viewport && (isVideoViewport || frameImageIds.length > 0)
              }
            />

            {activeMeasurement &&
              measurementStatus === 'awaiting' &&
              isActive && (
                <div className={styles.measurementIndicator}>
                  {activeMeasurement.toolType === 'simpson'
                    ? `Tracing: ${activeMeasurement.measurementLabel} (Simpson)`
                    : `Measuring: ${activeMeasurement.measurementLabel}`}
                </div>
              )}

            {/* Top-left overlay: view classification + FPS */}
            {viewport && (viewPrediction || isVideo) && (
              <div className={styles.topLeftOverlay}>
                {viewPrediction && (
                  <div className={styles.overlayBadge}>
                    {viewPrediction.window} {viewPrediction.view}
                  </div>
                )}
                {isVideo && nativeFps > 0 && (
                  <div className={styles.overlayBadge}>
                    {Math.round(nativeFps * cineSpeedMultiplier)} FPS
                  </div>
                )}
              </div>
            )}

            {viewport && (
              <MeasurementList
                dicomUuid={dicom.uuid}
                stackImageId={
                  isVideoViewport ? null : (memoizedImageIds?.[0] ?? null)
                }
                currentFrame={currentFrame}
              />
            )}

            {isActive && <ToolHint activeTool={activeTool} />}

            {/* Frame slider for multi-frame content */}
            {isVideo && viewport && (
              <FrameSlider
                frameCount={frameCount}
                currentFrame={currentFrame}
                onFrameChange={handleFrameChange}
                isPlaying={isPlaying}
                onPreviousFrame={handlePreviousFrame}
                onTogglePlayPause={togglePlayPause}
                onNextFrame={handleNextFrame}
                annotatedFrames={annotatedFrames}
                phaseKeyframes={phaseKeyframes}
              />
            )}
          </>
        )}
      </div>
    </Dropdown>
  );
}
